import {FC, useState, useRef, useEffect, useMemo, useCallback} from 'react';

import {t} from 'i18next';
import {useDispatch} from 'react-redux';

import {CarouselAnalyticsProps} from 'components/recommendations/carousel-list-container/types';
import {BASKET_RECOMMENDATION_CAROUSEL_NAME, isBasketDiversifyFlagEnabled} from 'constants/recommendation';
import {CarouselSize} from 'contexts/analytics-context';
import {useAppSelector} from 'hooks';
import {useAppMode} from 'hooks/useAppMode';
import {useCarouselEventHandler} from 'hooks/useCarouselEventHandler';
import ReactNative from 'services/react-native';
import SendBrowserEvent from 'services/send-browser-event';
import {getBasketCalculationAction} from 'store/actions/basket-action';
import {EventMessagesType, RecommendationResponse} from 'types';
import {productAnalytics} from 'utils';
import {getPreviewItemsWithBasketItems} from 'utils/basket';
import {geSearchCarouselUrl, getBasketRecommendationTitle} from 'utils/recommendation-utils/recommendations';

import {StyledIFrame} from './styles/styledIFrame.style';

export const RecommendationsCarouselMFE: FC = () => {
  const dispatch = useDispatch();
  const isReactNativeView = ReactNative.isReactNativeWebView();
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [isCarouselVisible, setIsCarouselVisible] = useState(false);
  const [isIframeReady, setIsIframeReady] = useState<boolean>(false);
  const [recommendations, setRecommendations] = useState<RecommendationResponse | null>(null);

  const {isExpress} = useAppMode();
  const title = useMemo(() => {
    if (!recommendations?.recommendationType) {
      return BASKET_RECOMMENDATION_CAROUSEL_NAME.personalised;
    }
    return t(getBasketRecommendationTitle(recommendations?.recommendationType));
  }, [recommendations?.recommendationType]);
  const isCarouselImpressionSent = useRef<boolean>(false);
  const impressionTimerRef = useRef<NodeJS.Timeout | null>(null);
  const CAROUSEL_IMPRESSION_DEFAULT_DELAY = 3000;

  const currentStore = useAppSelector((state) => state?.app?.currentStore);
  const {idToken: accessToken, customerId: userId} = useAppSelector((state) => state?.auth);
  const {basket} = useAppSelector((state) => state?.basket);

  const [isEmptyCarousel, setIsEmptyCarousel] = useState(false);

  const handleOnSeeAllClick = (analyticsPayload: any) => {
    !isReactNativeView && SendBrowserEvent.closeBasketDrawer();
    SendBrowserEvent.goToPLP('recommendations', analyticsPayload);
  };

  const productsAnalytics = useMemo(
    () =>
      recommendations?.products?.map((product) => {
        return {
          ...productAnalytics(product),
          ...(product?.recommendationScore !== undefined && {
            recommendationScore: product?.recommendationScore
          }),
          ...(product?.normalisedRecommendationScore !== undefined && {
            normalisedRecommendationScore: product?.normalisedRecommendationScore
          }),
          ...(product?.alreadyPurchased !== undefined && {
            hasAlreadyPurchased: product?.alreadyPurchased
          })
        };
      }),
    [recommendations?.products]
  );

  const addAnalyticsRecommendationCarouselImpression = useCallback(() => {
    if (isCarouselImpressionSent.current) return;
    isCarouselImpressionSent.current = true;

    const analyticsParams = {
      carouselName: title,
      algorithm: recommendations?.algorithm,
      getPersonalizedRecommendationsFromDb: recommendations?.recommendationType,
      isDiversifyResult: isBasketDiversifyFlagEnabled,
      isCarouselPersonalised: recommendations?.personalized,
      products: productsAnalytics,
      ...(isReactNativeView && {
        fromExpress: isExpress
      })
    };

    SendBrowserEvent.carouselImpressionEvent(analyticsParams);
  }, [
    recommendations?.algorithm,
    recommendations?.recommendationType,
    recommendations?.personalized,
    productsAnalytics,
    isReactNativeView,
    isExpress,
    title
  ]);

  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      if (event.data?.type === 'IFRAME_CONSOLE_LOG') {
        // Forward to React Native WebView
        if ((window as any).ReactNativeWebView) {
          (window as any).ReactNativeWebView.postMessage(JSON.stringify(event.data));
        }
      }
    }

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const triggerCarouselImpressionImmediately = useCallback(() => {
    if (!isCarouselImpressionSent.current) {
      if (impressionTimerRef.current) {
        clearTimeout(impressionTimerRef.current);
        impressionTimerRef.current = null;
      }
      addAnalyticsRecommendationCarouselImpression();
    }
  }, [addAnalyticsRecommendationCarouselImpression]);

  const triggerCarouselScrollAnalytics = (analyticsData: CarouselAnalyticsProps, scrollDirection?: string) => {
    const analyticsParams = {
      ...analyticsData,
      carouselName: title,
      algorithm: recommendations?.algorithm,
      getPersonalizedRecommendationsFromDb: recommendations?.recommendationType,
      isDiversifyResult: isBasketDiversifyFlagEnabled,
      ...(isReactNativeView && {
        fromExpress: isExpress
      }),
      ...(scrollDirection && {scrollDirection})
    };
    SendBrowserEvent.carouselScrollEvent(analyticsParams);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsCarouselVisible(entry.isIntersecting);
      },
      {threshold: 0.8} // Adjust threshold (80% visible)
    );

    if (iframeRef.current) observer.observe(iframeRef.current);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (isCarouselVisible && !isCarouselImpressionSent?.current) {
      impressionTimerRef.current = setTimeout(() => {
        addAnalyticsRecommendationCarouselImpression();
      }, CAROUSEL_IMPRESSION_DEFAULT_DELAY);
    }
    return () => {
      if (impressionTimerRef.current) clearTimeout(impressionTimerRef.current);
    };
  }, [basket?.basketItems?.length, isCarouselVisible, addAnalyticsRecommendationCarouselImpression]);

  useEffect(() => {
    iframeRef.current?.contentWindow?.postMessage(
      {
        type: EventMessagesType.REFRESH_BASKET
      },
      '*'
    );
  }, [basket?.basketItems]);

  const updateBasket = () => {
    dispatch(getBasketCalculationAction({isBasketPreviewItemsUpdated: true}));
  };

  const handleIframeReady = () => {
    setTimeout(() => {
      const previewBasketItems = basket?.basketItems?.length ? getPreviewItemsWithBasketItems(basket.basketItems) : [];

      iframeRef.current?.contentWindow?.postMessage(
        {
          type: EventMessagesType.BASKET_ITEMS,
          message: JSON.stringify(previewBasketItems)
        },
        '*'
      );
    }, 200);
  };

  useCarouselEventHandler({
    onSeeAllClick: handleOnSeeAllClick,
    onEmptyCarousel: (isEmpty: boolean) => {
      setIsEmptyCarousel(isEmpty);
    },
    updateBasket: updateBasket,
    triggerCarouselScrollAnalytics: triggerCarouselScrollAnalytics,
    setRecommendations: (recommendation: RecommendationResponse) => {
      setRecommendations(recommendation);
    },
    triggerImmediateImpression: triggerCarouselImpressionImmediately
  });

  const carouselUrl = useMemo(() => {
    if (!title) {
      return undefined;
    }
    if (!currentStore?.storeCodes?.length) {
      return undefined;
    }

    return geSearchCarouselUrl({
      title,
      stores: currentStore.storeCodes,
      accessToken,
      isExpressMode: isExpress,
      productVariant: CarouselSize.Normal,
      recommendationVariant: 'basketRecommendation',
      userId,
      sameDay: currentStore.isSameDayDelivery,
      platform: ReactNative.isReactNativeWebView() ? 'app' : 'web',
      isDiversify: isBasketDiversifyFlagEnabled
    });
  }, [currentStore?.storeCodes, currentStore?.isSameDayDelivery, accessToken, isExpress, userId, title]);

  if (isEmptyCarousel) {
    return null;
  }
  return (
    <StyledIFrame
      ref={iframeRef}
      isApp={isReactNativeView}
      src={carouselUrl}
      className={`iframeCarousel ${isIframeReady ? 'loaded' : 'not-loaded'}`}
      onLoad={() => {
        setIsIframeReady(true);
        handleIframeReady();
      }}
    />
  );
};
